import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { tokenStorage } from '../../../lib/storage/tokenStorage';
import { setUnauthorizedHandler } from '../../../lib/api/BaseApiClient';

const AuthContext = createContext(null);

// Deep-scan for a bearer token anywhere in the response — the API may put
// `jwttoken` / `jwtToken` / `token` / `accessToken` at the envelope level OR
// inside `result`, so we search both by any key containing "token".
const findToken = (obj, depth = 0) => {
  if (!obj || typeof obj !== 'object' || depth > 4) return null;
  // Prefer a key that looks like a token/jwt whose value is long enough to be one.
  for (const [k, v] of Object.entries(obj)) {
    if (typeof v === 'string' && v.length > 20 && /token|jwt|bearer/i.test(k)) return v;
  }
  for (const v of Object.values(obj)) {
    if (v && typeof v === 'object') {
      const t = findToken(v, depth + 1);
      if (t) return t;
    }
  }
  return null;
};

const extractToken = (payload) => findToken(payload);

// Normalise the user object from a ValidateOTP envelope or a plain user.
// The user fields live in `result` (or the payload itself if already unwrapped).
const extractUser = (payload) => {
  const r = (payload && typeof payload === 'object' && payload.result) ? payload.result : (payload ?? {});
  const src = (r && typeof r === 'object') ? r : {};
  return {
    userID: src.userID ?? src.userId ?? src.id ?? null,
    name: src.name ?? '',
    mobile: src.mobile ?? '',
    email: src.email ?? '',
    profileImage: src.profileImage ?? src.imageURL ?? null,
    ...src,
  };
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => tokenStorage.getUser());
  const [showLogin, setShowLogin] = useState(false);

  const logout = useCallback(() => {
    setUser(null);
    tokenStorage.clear();
  }, []);

  // 401 no longer force-clears the session (that logged users out on reload).
  // We only log it; the failing request surfaces its own error to the caller.
  useEffect(() => {
    setUnauthorizedHandler((url) => {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn('[auth] 401 from', url, '— session kept; request failed.');
      }
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  // Diagnostic: on load, report what was restored from storage.
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.log('[auth] session restored on load →', {
        user: tokenStorage.getUser(),
        hasToken: !!tokenStorage.getToken(),
      });
    }
  }, []);

  // Accepts a raw ValidateOTP envelope (preferred — carries the token) or a
  // plain user object. Persists token + user so the session survives reload.
  const login = useCallback((payload) => {
    const token = extractToken(payload);
    const u = extractUser(payload);
    if (token) {
      tokenStorage.setToken(token);
      u.token = token;
    }
    tokenStorage.setUser(u);
    setUser(u);
    setShowLogin(false);
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.log('[auth] login() →', {
        receivedPayload: payload,
        extractedToken: token ? `${String(token).slice(0, 12)}…` : null,
        extractedUser: u,
        persistedUser: tokenStorage.getUser(),
        persistedToken: !!tokenStorage.getToken(),
      });
    }
  }, []);

  // Merge partial updates (e.g. after profile edit) into the stored user.
  const updateUser = useCallback((patch) => {
    setUser((prev) => {
      const next = { ...(prev || {}), ...patch };
      tokenStorage.setUser(next);
      return next;
    });
  }, []);

  const openLogin = useCallback(() => setShowLogin(true), []);
  const closeLogin = useCallback(() => setShowLogin(false), []);

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated: !!user,
      login,
      logout,
      updateUser,
      showLogin,
      openLogin,
      closeLogin,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
};
